import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Plus, Loader2, X, UserCheck } from 'lucide-react'
import { adminApi, CreateRepresentativeRequest } from '../../services/api'

export default function RepresentativesPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showDialog, setShowDialog] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['admin-representatives'],
    queryFn: () => adminApi.listRepresentatives({ pageSize: 100 }).then(r => r.data),
  })

  const reps = data?.items || []

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Przedstawiciele</h2>
        <button onClick={() => setShowDialog(true)} className="btn-primary py-2 px-4 flex items-center gap-2">
          <Plus className="w-4 h-4" />
          Nowy przedstawiciel
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nazwa</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Firma</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Prowizja</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Klienci</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Kody</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {reps.map((rep) => (
                <tr
                  key={rep.id}
                  className="hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/admin/representatives/${rep.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UserCheck className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-sm">{rep.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{rep.email}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{rep.companyName || '—'}</td>
                  <td className="px-4 py-3 text-sm font-medium">{(rep.commissionRate * 100).toFixed(0)}%</td>
                  <td className="px-4 py-3 text-sm">{rep.activeCustomersCount}</td>
                  <td className="px-4 py-3 text-sm">{rep.totalCodesCount}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${
                      rep.isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {rep.isActive ? 'Aktywny' : 'Nieaktywny'}
                    </span>
                  </td>
                </tr>
              ))}
              {reps.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-400">
                    Brak przedstawicieli
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {showDialog && (
        <CreateRepDialog
          onClose={() => setShowDialog(false)}
          onCreated={() => {
            setShowDialog(false)
            queryClient.invalidateQueries({ queryKey: ['admin-representatives'] })
          }}
        />
      )}
    </div>
  )
}

function CreateRepDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState<CreateRepresentativeRequest>({
    name: '',
    email: '',
    commissionRate: 0.1,
  })
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (data: CreateRepresentativeRequest) => adminApi.createRepresentative(data),
    onSuccess: () => onCreated(),
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setError(err.response?.data?.error || 'Nie udało się utworzyć przedstawiciela.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim() || !form.email.trim()) {
      setError('Nazwa i email są wymagane.')
      return
    }
    mutation.mutate(form)
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">Nowy przedstawiciel</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Imię i nazwisko *</label>
            <input
              type="text"
              value={form.name}
              onChange={e => setForm({ ...form, name: e.target.value })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
            <input
              type="email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="input w-full"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
              <input
                type="tel"
                value={form.phoneNumber || ''}
                onChange={e => setForm({ ...form, phoneNumber: e.target.value || undefined })}
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Prowizja (%)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                value={form.commissionRate}
                onChange={e => setForm({ ...form, commissionRate: parseFloat(e.target.value) || 0 })}
                className="input w-full"
              />
              <p className="text-xs text-gray-400 mt-0.5">np. 0.10 = 10%</p>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firma</label>
            <input
              type="text"
              value={form.companyName || ''}
              onChange={e => setForm({ ...form, companyName: e.target.value || undefined })}
              className="input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notatki</label>
            <textarea
              value={form.notes || ''}
              onChange={e => setForm({ ...form, notes: e.target.value || undefined })}
              className="input w-full"
              rows={2}
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary py-2 px-4">
              Anuluj
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary py-2 px-4 flex items-center gap-2">
              {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Utwórz
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
